// onnxruntime-web とモデルの取得・実行。ネットワークアクセスは同意 (consent) がある場合だけ行い、
// 取得したものは Cache API に保存して 2 回目以降は再取得しない。
import { ref } from 'vue'
import { AI_MODELS, CONSENT_KEY, ORT_BASE, ORT_MODULE_URL, getAiModel, parseConsent, preprocess, postprocess, type AiConsent, type AiModelDef } from './models'

const CACHE_NAME = 'bpe-ai-v1'

export const aiConsent = ref<AiConsent | null>(typeof localStorage !== 'undefined' ? parseConsent(localStorage.getItem(CONSENT_KEY)) : null)
export const aiProgress = ref<{ label: string; ratio: number } | null>(null)

export function hasAiConsent(): boolean {
  return aiConsent.value !== null
}
export function giveAiConsent(modelId: string): void {
  if (!getAiModel(modelId)) throw new Error('未知のモデル')
  const c: AiConsent = { version: 1, agreedAt: Date.now(), modelId }
  aiConsent.value = c
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(c))
  } catch {
    /* ignore */
  }
}
export function revokeAiConsent(): void {
  aiConsent.value = null
  try {
    localStorage.removeItem(CONSENT_KEY)
  } catch {
    /* ignore */
  }
  sessions.clear()
}
export async function clearAiCache(): Promise<void> {
  sessions.clear()
  ortModule = null
  if (typeof caches !== 'undefined') await caches.delete(CACHE_NAME)
}

/** 取得 (Cache API 優先)。進捗を aiProgress に出す */
async function fetchCached(url: string, label: string, expectedBytes: number): Promise<ArrayBuffer> {
  const cache = typeof caches !== 'undefined' ? await caches.open(CACHE_NAME).catch(() => null) : null
  if (cache) {
    const hit = await cache.match(url)
    if (hit) return hit.arrayBuffer()
  }
  const res = await fetch(url)
  if (!res.ok) throw new Error(`${label}: HTTP ${res.status}`)
  const total = Number(res.headers.get('content-length')) || expectedBytes
  const reader = res.body?.getReader()
  let buf: ArrayBuffer
  if (reader) {
    const chunks: Uint8Array[] = []
    let got = 0
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      got += value.byteLength
      aiProgress.value = { label, ratio: total ? Math.min(1, got / total) : 0 }
    }
    const all = new Uint8Array(got)
    let o = 0
    for (const c of chunks) {
      all.set(c, o)
      o += c.byteLength
    }
    buf = all.buffer
  } else buf = await res.arrayBuffer()
  if (cache) await cache.put(url, new Response(buf.slice(0), { headers: { 'content-type': 'application/octet-stream' } })).catch(() => undefined)
  return buf
}

type OrtLike = {
  env: { wasm: { wasmPaths: string; numThreads: number } }
  Tensor: new (type: 'float32', data: Float32Array, dims: number[]) => { data: Float32Array; dims: number[] }
  InferenceSession: { create: (buf: ArrayBuffer | Uint8Array, opts: { executionProviders: string[] }) => Promise<OrtSession> }
}
interface OrtSession {
  inputNames: string[]
  outputNames: string[]
  run: (feeds: Record<string, unknown>) => Promise<Record<string, { data: Float32Array; dims: number[] }>>
}
let ortModule: OrtLike | null = null
const sessions = new Map<string, OrtSession>()

async function ensureOrt(): Promise<OrtLike> {
  if (ortModule) return ortModule
  if (!hasAiConsent()) throw new Error('consent-required')
  aiProgress.value = { label: 'onnxruntime-web', ratio: 0 }
  // CDN から ES モジュールを読み込む (Vite にはバンドルさせない)
  const mod = (await import(/* @vite-ignore */ ORT_MODULE_URL)) as unknown as OrtLike
  mod.env.wasm.wasmPaths = ORT_BASE
  mod.env.wasm.numThreads = typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated ? Math.min(4, navigator.hardwareConcurrency || 1) : 1
  ortModule = mod
  return mod
}

async function getSession(def: AiModelDef): Promise<OrtSession> {
  const hit = sessions.get(def.id)
  if (hit) return hit
  const ort = await ensureOrt()
  const bytes = await fetchCached(def.url, def.name, def.bytes)
  aiProgress.value = { label: def.name, ratio: 1 }
  const s = await ort.InferenceSession.create(new Uint8Array(bytes), { executionProviders: ['wasm'] })
  sessions.set(def.id, s)
  return s
}

/** マッティング: RGBA (文書解像度) → 0..255 のアルファ (文書解像度) */
export async function runMatting(rgba: Uint8ClampedArray, width: number, height: number, modelId?: string): Promise<Uint8ClampedArray> {
  if (!hasAiConsent()) throw new Error('consent-required')
  const def = getAiModel(modelId ?? aiConsent.value!.modelId) ?? AI_MODELS[0]!
  try {
    const ort = await ensureOrt()
    const session = await getSession(def)
    const pre = preprocess(rgba, width, height, def)
    aiProgress.value = { label: def.name, ratio: 1 }
    const input = new ort.Tensor('float32', pre.data, [1, 3, pre.h, pre.w])
    const out = await session.run({ [session.inputNames[0]!]: input })
    const first = out[session.outputNames[0]!]!
    const dims = first.dims
    const oh = dims[dims.length - 2]!, ow = dims[dims.length - 1]!
    return postprocess(first.data, ow, oh, width, height, def)
  } finally {
    aiProgress.value = null
  }
}
